import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { AspectRatioWrapper, IconArrowHead, ResponsiveImage } from '../../components';

import css from './ListingPage.module.css';

const IMAGE_ASPECT_WIDTH = 587;
const IMAGE_ASPECT_HEIGHT = 750;

const SCALED_VARIANTS = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];
const CARD_VARIANTS = ['listing-card', 'listing-card-2x', 'listing-card-4x', 'listing-card-6x'];

const getAvailableVariants = image => {
  const variants = image?.attributes?.variants || {};
  const scaled = SCALED_VARIANTS.filter(name => variants[name]);
  return scaled.length > 0 ? scaled : CARD_VARIANTS.filter(name => variants[name]);
};

const IMAGE_SIZES = '(max-width: 1024px) 100vw, 36vw';

const GalleryImage = React.memo(props => {
  const { image, alt } = props;
  const variants = image ? getAvailableVariants(image) : [];
  return (
    <AspectRatioWrapper
      className={css.galleryItem}
      width={IMAGE_ASPECT_WIDTH}
      height={IMAGE_ASPECT_HEIGHT}
    >
      <ResponsiveImage
        rootClassName={css.galleryImage}
        alt={alt}
        image={image}
        variants={variants}
        sizes={IMAGE_SIZES}
      />
    </AspectRatioWrapper>
  );
});

/**
 * Listing photos as a horizontal gallery. Previous sits on the left and next
 * on the right, including the first and last photos. Previous is inactive on
 * the first photo; next is inactive on the last.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing the listing entity
 * @returns {JSX.Element}
 */
const SectionGallery = props => {
  const intl = useIntl();
  const { listing } = props;
  const images = listing.images || [];
  const swiperRef = useRef(null);
  const [edges, setEdges] = useState({ isBeginning: true, isEnd: images.length <= 1 });

  // Swiper is ESM-only. Staging SSR (Node + webpack-node-externals) cannot
  // require() it, so load it on the client after mount. The first image is
  // shown statically until it arrives.
  const [swiperLib, setSwiperLib] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([import('swiper/react'), import('swiper/css')])
      .then(([swiperReact]) => {
        if (!cancelled) {
          setSwiperLib({
            Swiper: swiperReact.Swiper,
            SwiperSlide: swiperReact.SwiperSlide,
          });
        }
      })
      .catch(err => {
        console.warn('Failed to load Swiper', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const altFor = (index, count) =>
    intl.formatMessage({ id: 'ListingImageGallery.imageAltText' }, { index, count });

  const syncEdges = swiper => {
    setEdges({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd });
  };

  const handleSwiperInit = swiper => {
    swiperRef.current = swiper;
    syncEdges(swiper);
  };

  const { Swiper, SwiperSlide } = swiperLib || {};
  const showNav = images.length > 1;

  if (images.length === 0) {
    return (
      <section className={css.productGallery} data-testid="carousel">
        <GalleryImage image={null} alt="" />
      </section>
    );
  }

  return (
    <section className={css.productGallery} data-testid="carousel">
      {Swiper ? (
        <div className={css.galleryFrame}>
          <Swiper
            direction="horizontal"
            loop={false}
            speed={400}
            slidesPerView={1}
            spaceBetween={10}
            className={css.galleryCarousel}
            onSwiper={handleSwiperInit}
            onSlideChange={syncEdges}
          >
            {images.map((image, index) => (
              <SwiperSlide key={image.id?.uuid || index}>
                <GalleryImage image={image} alt={altFor(index + 1, images.length)} />
              </SwiperSlide>
            ))}
          </Swiper>
          {showNav ? (
            <button
              type="button"
              className={classNames(css.galleryNav, css.galleryNavPrev)}
              aria-label={intl.formatMessage({ id: 'ListingImageGallery.previous' })}
              disabled={edges.isBeginning}
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <IconArrowHead direction="left" size="small" />
            </button>
          ) : null}
          {showNav ? (
            <button
              type="button"
              className={classNames(css.galleryNav, css.galleryNavNext)}
              aria-label={intl.formatMessage({ id: 'ListingImageGallery.next' })}
              disabled={edges.isEnd}
              onClick={() => swiperRef.current?.slideNext()}
            >
              <IconArrowHead direction="right" size="small" />
            </button>
          ) : null}
        </div>
      ) : (
        <div className={css.galleryItem}>
          <GalleryImage image={images[0]} alt={altFor(1, images.length)} />
        </div>
      )}
    </section>
  );
};

export default React.memo(SectionGallery);
